<script setup lang="ts">
import TeemillLink from '@/components/TeemillLink.vue';
import { useProject, useProjects } from '@/services/projects';
import { useUser } from '@/services/user';
import { useBreakpoints } from '@/utils/breakpoints';
import { faArrowUpRight, faCircleNotch, faMailbox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@teemill/components';
import type { GetNotificationsMeta200ResponseUnreadCountByGroupInner } from '@teemill/dashboard-user';
import { useLoading } from '@teemill/modules/utils';
import type { Project } from '@teemill/projects';
import { computed, onMounted, ref } from 'vue';
import TeemillProjectIcon from './TeemillProjectIcon.vue';

const breakpoints = useBreakpoints();
const greaterOrEqualToSm = breakpoints.greaterOrEqual('sm');

const activeProject = useProject();
const { all: allProjects } = useProjects();
const user = useUser();
const unreadCountByGroup = ref<GetNotificationsMeta200ResponseUnreadCountByGroupInner[]>([]);
const fetching = useLoading();

const fetchUnreadCountByGroup = () => {
  user.notifications.meta({breakdown: true})
    .then((meta) => {
      unreadCountByGroup.value = meta.unreadCountByGroup ?? [];
    }).finally(() => {
      fetching.value = false;
    });
};

const projectGroups = computed(() =>
  unreadCountByGroup.value
    .filter((group) => group.group !== `project:${activeProject.value?.id}`)
    .map((group) => ({
      count: group.unreadCount,
      project: allProjects.find((p) => p.id === group.group.replace('project:', '')),
    }))
    .filter(({ project }) => project !== undefined) as { count: number; project: Project }[],
);

onMounted(fetchUnreadCountByGroup);

</script>

<template>
  <div
    class="flex flex-col overflow-y-auto overflow-x-hidden min-h-[217px] sm:max-h-[65vh]"
    :class="{ 'h-[60vh]': !greaterOrEqualToSm }"
  >
    <template v-if="fetching">
      <div class="text-center m-auto">
        <FontAwesomeIcon :icon="faCircleNotch" spin class="text-4xl mb-3" />
        <p>Loading notifications...</p>
      </div>
    </template>
    <template v-else>
      <TeemillLink
        v-for="{ count, project } in projectGroups"
        :key="project.id"
        class="border-b-[1px] border-b-[--tml-tabs-border-color] last:border-b-0"
        :to="{ name: 'projects.switch', query: { project: project.id, notification: 'true' } }"
      >
        <div class="p-3 flex gap-3 items-center">
          <TeemillProjectIcon class="!p-0.5 w-6 h-6" :project />
          <p class="flex-grow">
            <span class="font-bold">{{ project.name }}</span> has
            <span class="font-bold">{{ count }}</span> notification{{ count > 1 ? 's' : '' }}
          </p>
          <FontAwesomeIcon class="text-xl" :icon="faArrowUpRight" />
        </div>
      </TeemillLink>

      <div v-if="projectGroups.length === 0" class="text-center m-auto">
        <FontAwesomeIcon :icon="faMailbox" class="text-4xl mb-3" />
        <p>You're all caught up</p>
      </div>
    </template>
  </div>
</template>
